import { defineStore } from "pinia";
import api from "@/services/api";
import { router } from "@/services/router.ts";
import TranslationsService from "@/services/translations.ts";
import { loadOrganization, loadIdentity } from '@/services/storeLoader';
import { useSessionStore, useUserSettingsStore, useUsersStore } from '@/stores';
import type IAccessTokenResponse from "../models/responses/IAccessTokenResponse.ts";
import type IUser from "../models/responses/IUser.ts";
import UserRegisterRequest from "@/models/requests/UserRegisterRequest.ts";
import User from "@/models/frontend/User.ts";

// Function to store token details securely
function storeTokenData(data: IAccessTokenResponse): void {
  localStorage.setItem('access-token', data.accessToken);
  localStorage.setItem('access-token-type', data.tokenType ?? "Bearer");
  localStorage.setItem('access-token-expiry', (Date.now() + (data.expiresIn * 1000)).toString()); // Convert to timestamp
}

function getAccessToken(): string | null {
  const token = localStorage.getItem('access-token');
  const expiresAt = localStorage.getItem('access-token-expiry');

  if (token && expiresAt && Date.now() < Number(expiresAt)) {
    return token;
  }
  return null;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    returnUrl: null as string | null,
    errorMessage: "",
    user: null as User | null,
    isAuthenticated: !!getAccessToken(),
  }),
  actions: {
    resetAll() {
      localStorage.removeItem('access-token');
      localStorage.removeItem('access-token-type');
      localStorage.removeItem('access-token-expiry');
      this.user = null;
      this.isAuthenticated = false;
      this.errorMessage = "";
    },
    async login(email: string, password: string) {
      // this request sets the "refresh_token" cookie implicitly
      const loginResponse = await api.post<IAccessTokenResponse>("v1.0/auth/login", {
        email: email,
        password: password,
      });

      await this.loginWithToken(loginResponse.data);
    },
    async loginWithCodeToken(token: string) {
      // this request sets the "refresh_token" cookie implicitly
      const loginResponse = await api.post<IAccessTokenResponse>("v1.0/auth/login-token", {
        token: token
      });

      await this.loginWithToken(loginResponse.data);
    },
    async loginWithToken(loginResponse: IAccessTokenResponse) {
      storeTokenData(loginResponse);

      this.isAuthenticated = true;

      // after login get user details and jwt token
      const usersStore = useUsersStore();
      const axMeResponse = await api.get<IUser>("v1.0/users/.self");
      this.user = usersStore.mapToFrontend(axMeResponse.data);
      
      await loadOrganization();
      await loadIdentity();

      const sessionStore = useSessionStore();
      const userSettingsStore = useUserSettingsStore();

      if (userSettingsStore.data.lastUsedSiteId > 0) {
        await sessionStore.selectSite(userSettingsStore.data.lastUsedSiteId);
      }

      await TranslationsService.changeLocale(userSettingsStore.data.cultureName);

      // redirect to previous url or default to home page
      router.push(this.returnUrl || { name: 'home' });
    },
    async implicitLogin() {
      const accessToken = localStorage.getItem('access-token');
      if (!accessToken) { // possibly also no refresh_token cookie, don't bother trying to refresh
        return;
      }
  
      try {
        const usersStore = useUsersStore();
        const axMeResponse = await api.get<IUser>("v1.0/users/.self");
        this.user = usersStore.mapToFrontend(axMeResponse.data);

        // if this can be reached, the token or refresh token is still valid (session was not signed out)
        this.isAuthenticated = true;

        await loadOrganization();
        await loadIdentity();

        const sessionStore = useSessionStore();
        const userSettingsStore = useUserSettingsStore();

        if (userSettingsStore.data.lastUsedSiteId > 0) {
          await sessionStore.selectSite(userSettingsStore.data.lastUsedSiteId);
        }

        await TranslationsService.changeLocale(userSettingsStore.data.cultureName);

      } catch (error) {
        this.isAuthenticated = false;
      }
    },
    sendPasswordResetEmail(email: string) { 
      const locale = TranslationsService.getUserLocale();
      return api.post(`v1.0/auth/forgot-password?culture=${locale.locale}`, { email });
    },
    resetPassword(email: string, token: string, password: string) {
      const locale = TranslationsService.getUserLocale();
      return api.post<boolean>(`v1.0/auth/reset-password?culture=${locale.locale}`, { email, token, password });
    },
    async register(request: UserRegisterRequest) {
      const locale = TranslationsService.getUserLocale();
      return await api.post(`v1.0/auth/register?culture=${locale.locale}`, request)
      .catch((error) => (this.errorMessage = error));
    },
  },
});
